import { CodeBlock as Base } from "@corca/tiptap";
import { isExecutableLanguage } from "@corca/tiptap";
import { backtickInputRegex, tildeInputRegex } from "@tiptap/extension-code-block";
import { ReactNodeViewRenderer, textblockTypeInputRule } from "@tiptap/react";
import { nanoid } from "nanoid";

import { CodeBlockLineWrapper } from "@/components/tiptap/code-block/CodeBlockLineWrapper";

import type { CodeBlockOptions } from "./code-block/CodeBlockOptions";
import type { EditorState } from "@tiptap/pm/state";
import type { NodeViewProps, NodeViewRendererProps } from "@tiptap/react";

export type RegisterSelectionSetter = (fn: ((anchor: number, head: number) => void) | null) => void;

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    extendedCodeBlock: {
      appendCodeBlock: (position?: number, attrs?: { language: string }) => ReturnType;
    };
  }
}

export type { CodeBlockOptions };

export function resolveCellGroup(language: string, state?: EditorState) {
  if (!isExecutableLanguage(language)) return undefined;
  if (!state) return "group1";

  const { selection } = state;
  const index = selection.$from.index();

  // Find the nearest previous code block with a cell group
  for (let i = index - 1; i >= 0; i--) {
    const node = state.doc.child(i);
    if (node && node.type.name === "codeBlock" && node.attrs.cellGroup) {
      return node.attrs.cellGroup as string;
    }
  }

  // Default to "group1" if no previous code block found
  return "group1";
}

export const CodeBlock = Base.extend<CodeBlockOptions>({
  addNodeView() {
    return (props: NodeViewRendererProps) => {
      /**
       * Bridge between PM's synchronous NodeView API and the async React component lifecycle.
       * PM calls nodeView.setSelection() synchronously, but the CodeMirror EditorView lives
       * inside a React component that hasn't mounted yet at that point. The closure lets
       * CodeMirrorEditor register its dispatch function once CM is ready, so subsequent
       * setSelection calls are forwarded correctly.
       * Pattern from https://prosemirror.net/examples/codemirror/
       */
      let cmSetter: ((anchor: number, head: number) => void) | null = null;
      /**
       * It's an ugly fix to focus code block after copy/paste.
       * Stores a setSelection call that arrived before CM mounted.
       * Replayed when registerSelectionSetter receives the real setter.
       */
      let pendingSelection: { anchor: number; head: number } | null = null;

      const registerSelectionSetter: RegisterSelectionSetter = (fn) => {
        cmSetter = fn;

        if (fn !== null && pendingSelection !== null) {
          fn(pendingSelection.anchor, pendingSelection.head);
          pendingSelection = null;
        }
      };

      function NodeViewComponent(nodeViewProps: NodeViewProps) {
        return (
          <CodeBlockLineWrapper
            {...nodeViewProps}
            registerSelectionSetter={registerSelectionSetter}
          />
        );
      }

      const nodeView = ReactNodeViewRenderer(NodeViewComponent, {
        className: "w-full mx-auto max-w-content-width sm:max-w-full",
      })(props);

      nodeView.setSelection = (anchor: number, head: number) => {
        if (cmSetter) {
          cmSetter(anchor, head);
        } else {
          pendingSelection = { anchor, head };
        }
      };

      return nodeView;
    };
  },

  addInputRules() {
    return [
      textblockTypeInputRule({
        find: backtickInputRegex,
        type: this.type,
        getAttributes: (match) => ({
          language: match[1] ?? "python",
          cellId: nanoid(),
          cellGroup: resolveCellGroup(match[1] ?? "python", this.editor.state),
        }),
      }),
      textblockTypeInputRule({
        find: tildeInputRegex,
        type: this.type,
        getAttributes: (match) => ({
          language: match[1] ?? "python",
          cellId: nanoid(),
          cellGroup: resolveCellGroup(match[1] ?? "python", this.editor.state),
        }),
      }),
    ];
  },

  addKeyboardShortcuts() {
    return {};
  },

  addCommands() {
    return {
      toggleCodeBlock:
        (attrs) =>
        ({ commands, state }) => {
          return commands.toggleNode("paragraph", this.name, {
            language: "python",
            cellId: nanoid(),
            cellGroup: resolveCellGroup("python", state),
            ...attrs,
          });
        },
      setCodeBlock:
        (attrs) =>
        ({ commands, state }) =>
          commands.setNode(this.name, {
            language: "python",
            cellId: nanoid(),
            cellGroup: resolveCellGroup("python", state),
            ...attrs,
          }),
      appendCodeBlock:
        (position, attrs) =>
        ({ commands, state }) => {
          return commands.insertContentAt(
            position !== undefined ? position : state.selection.$to.end(1),
            {
              type: this.name,
              attrs: {
                language: "python",
                cellId: nanoid(),
                cellGroup: resolveCellGroup("python", state),
                ...attrs,
              },
            },
          );
        },
    };
  },
});
